import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentUser } from '../contexts/CurrentUserContext';

export default function TrialBanner({ setFilter, onUpgrade }) {
  const { currentUser } = useCurrentUser();
  const { t } = useTranslation('common');

  const info = useMemo(() => {
    if (!currentUser) return null;

    // Covered members (household or a non-owner team member) are billed via
    // someone else's subscription — never show them the trial nudge.
    if (currentUser.isCoveredMember) return null;

    // A user who HAS access and is NOT on a trial has no trial to warn about. This
    // covers never-expire grants (staff / comp_open / reviewer programmes) which have
    // hasAccess:true, no subscription and isTrialing:false — they were wrongly shown
    // "your trial ends today" because the old check only hid an ACTIVE subscription.
    // Trialing users (isTrialing:true) and blocked users (hasAccess:false) still see it.
    if (currentUser.hasAccess === true && currentUser.isTrialing !== true) return null;

    const status = currentUser.subscriptionStatus;
    // Hide for active paid subscriptions
    if (status === 'active') return null;

    // The trial length is the server's (currentUser.trialDays, from lib/planAccess TRIAL_DAYS),
    // never a number written here. Without it (an older cached currentUser) no banner is shown.
    const trialDays = Number(currentUser.trialDays);
    if (!Number.isFinite(trialDays) || trialDays <= 0) return null;
    const DAY_MS = 24 * 60 * 60 * 1000;
    const trialEnd = currentUser.trialEndsAt
      ? new Date(currentUser.trialEndsAt).getTime()
      : new Date(currentUser.createdAt).getTime() + trialDays * DAY_MS;

    const daysLeft = Math.max(0, Math.ceil((trialEnd - Date.now()) / DAY_MS));
    const daysPassed = trialDays - daysLeft;

    // Show nothing in the first 10 days
    if (daysPassed < 10 && status !== 'trialing') return null;
    if (status === 'trialing' && daysLeft > 4) return null;

    // Card at sign-up, phase 4: a Stripe trial has a card on file, so it is charged unless cancelled.
    // "Choose a plan to keep access" was wrong for it (the plan is chosen); it says so instead.
    return { daysLeft, cardOnFile: status === 'trialing' && !!currentUser.stripeSubscriptionId };
  }, [currentUser]);

  if (!info) return null;

  const { daysLeft, cardOnFile } = info;
  const isUrgent = daysLeft <= 1;
  const isWarning = daysLeft <= 3;

  const bg = isUrgent ? '#dc2626' : isWarning ? '#d97706' : '#0C2D48';
  const text = cardOnFile
    ? (isUrgent && daysLeft < 1 ? t('trial.card_ends_today') : daysLeft === 1 ? t('trial.card_ends_in_one') : t('trial.card_ends_in_other', { count: daysLeft }))
    : isUrgent
    ? t('trial.ends_today')
    : daysLeft === 1
      ? t('trial.ends_in_one')
      : t('trial.ends_in_other', { count: daysLeft });

  return (
    <div
      style={{
        background: bg,
        color: '#fff',
        padding: '0.55rem 1.25rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '1.25rem',
        fontSize: '0.85rem',
        fontFamily: "'Raleway', sans-serif",
        fontWeight: 600,
        zIndex: 'var(--z-sticky)',
        flexShrink: 0,
      }}
    >
      <span>{text}</span>
      <button
        onClick={() => {
          if (onUpgrade) {
            onUpgrade();
            return;
          }
          if (setFilter) setFilter('Settings');
        }}
        aria-label={cardOnFile ? t('trial.plan_billing') : t('trial.choose_plan')}
        style={{
          background: 'rgba(255,255,255,0.2)',
          border: '1.5px solid rgba(255,255,255,0.5)',
          color: '#fff',
          padding: '0.3rem 1rem',
          borderRadius: '20px',
          fontFamily: "'Raleway', sans-serif",
          fontWeight: 700,
          fontSize: '0.8rem',
          cursor: 'pointer',
          whiteSpace: 'nowrap',
          transition: 'background 0.15s',
        }}
        onMouseOver={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.3)')}
        onMouseOut={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.2)')}
      >
        {cardOnFile ? t('trial.plan_billing') : t('trial.choose_plan')}
      </button>
    </div>
  );
}
